import { useInfiniteQuery } from "@tanstack/react-query";
import { OWGameResponse } from "@devour/client";
import getConfig from "../../utils/getConfig";

interface SearchEnhancedGamesResponse {
    data: OWGameResponse[];
    paginationInfo: {
        total: number;
        limit: number;
        offset: number;
    };
}

interface UseGameSearchInfiniteOptions {
    search?: string;
    category?: "all" | "popular" | "tracked" | "events";
    initialLimit?: number;
    showAllCount?: boolean; // Whether to show total count from first API call
}

async function fetchGamesPage(
    pageParam: number,
    search?: string,
    category?: string,
    limit: number = 50,
): Promise<SearchEnhancedGamesResponse> {
    const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (pageParam * limit).toString(),
    });

    // Add search if provided
    if (search && search.trim()) {
        params.set("search", search.trim());
    }

    // Add category filters
    switch (category) {
        case "popular":
            params.set("seoEnabled", "true");
            break;
        case "tracked":
            params.set("timeTrackedGame", "true");
            break;
        case "events":
            params.set("hasEvents", "true");
            break;
        case "all":
        default:
            // No additional filters - show all games with slugs
            break;
    }

    const response = await fetch(`${getConfig().basePath}/overwolf/enhanced-games/search?${params}`);

    if (!response.ok) {
        throw new Error(`Failed to fetch games: ${response.statusText}`);
    }

    const data: SearchEnhancedGamesResponse = await response.json();
    return data;
}

export const useGameSearchInfinite = (options: UseGameSearchInfiniteOptions = {}) => {
    const { search, category = "all", initialLimit = 100 } = options;

    const query = useInfiniteQuery({
        queryKey: ["games-infinite", search, category, initialLimit],
        queryFn: ({ pageParam = 0 }) => fetchGamesPage(pageParam, search, category, initialLimit),
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.length * initialLimit;
            const hasMore = totalLoaded < lastPage.paginationInfo.total;
            return hasMore ? allPages.length : undefined;
        },
        staleTime: 24 * 60 * 60 * 1000, // 24 hours (games are static reference data)
        gcTime: 48 * 60 * 60 * 1000, // 48 hours in cache
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    // Flatten all pages into a single games array
    const games = query.data?.pages.flatMap(page => page.data.filter(game => game && game.slug)) || [];

    // Get total count from the first page
    const totalGames = query.data?.pages[0]?.paginationInfo.total || 0;

    return {
        games,
        totalGames,
        isLoading: query.isLoading,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage,
        fetchNextPage: query.fetchNextPage,
        error: query.error ? "Failed to load games" : null,
        isError: query.isError,
    };
};